import { useEffect, useMemo, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookText, Truck, Users, Printer, FileDown, ArrowLeft, Search } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const csvDownload = (filename, headers, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map(r => r.map(esc).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

// Shared list view: name / mobile / GSTIN / outstanding + search
function PartyList({ rows, search, setSearch, onPick, nameLabel, prefix }) {
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r =>
      (r.name || "").toLowerCase().includes(s) ||
      (r.phone || "").toLowerCase().includes(s) ||
      (r.gstin || "").toLowerCase().includes(s));
  }, [rows, search]);
  const pg = usePagination(filtered, search);
  return (
    <div>
      <div className="mb-3 max-w-md">
        <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Search</Label>
        <div className="relative mt-1">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input data-testid={`${prefix}-search`} value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${nameLabel.toLowerCase()} / mobile / GSTIN…`} className="rounded-sm bg-secondary border-border h-9 pl-8"/>
        </div>
      </div>
      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${prefix}-list-table`}>
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">{nameLabel}</th>
              <th className="text-left p-3">Mobile</th>
              <th className="text-left p-3">GSTIN</th>
              <th className="text-right p-3">Outstanding Balance</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No records found</td></tr>}
            {pg.pageItems.map(r => (
              <tr key={r.id} data-testid={`${prefix}-list-row-${r.id}`} className="border-t border-border hover:bg-secondary/40">
                <td className="p-3">
                  <button type="button" data-testid={`${prefix}-link-${r.id}`} onClick={() => onPick(r.id)}
                    className="text-primary hover:underline font-medium text-left">{r.name}</button>
                </td>
                <td className="p-3 font-mono text-xs">{r.phone || "-"}</td>
                <td className="p-3 font-mono text-xs text-muted-foreground uppercase">{r.gstin || "-"}</td>
                <td className={`p-3 text-right font-mono font-semibold ${r.outstanding > 0 ? "text-destructive" : ""}`}>{inr(Math.abs(r.outstanding))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}

// Shared ledger detail: header + date range + Print/Export + spec table + Back
function LedgerDetail({ title, subtitle, data, from, to, setFrom, setTo, onBack, prefix, cards }) {
  const pg2 = usePagination(data?.rows || [], data);
  const exportCsv = () => {
    if (!data) return;
    csvDownload(`${prefix}_ledger_${(title || "party").replace(/\s+/g, "_")}.csv`,
      ["Date", "Reference No", "Transaction Type", "Voucher No", "Debit", "Credit", "Running Balance"],
      data.rows.map(r => {
        const isVoucher = r.voucher_no && r.voucher_no !== "-";
        return [r.date, isVoucher ? "-" : r.ref, r.particulars, r.voucher_no || "-", r.dr || "", r.cr || "", r.balance];
      }));
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-3 print:mb-2">
        <div className="flex items-center gap-3">
          <Button type="button" data-testid={`${prefix}-back`} variant="outline" onClick={onBack} className="rounded-sm text-xs uppercase tracking-wider print:hidden"><ArrowLeft className="w-3 h-3 mr-1"/>Back</Button>
          <div>
            <div className="font-display uppercase text-lg" data-testid={`${prefix}-detail-name`}>{title}</div>
            {subtitle && <div className="text-[11px] text-muted-foreground">{subtitle}</div>}
          </div>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button data-testid={`${prefix}-export`} variant="outline" onClick={exportCsv} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> Export CSV</Button>
          <Button data-testid={`${prefix}-print`} variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 border border-border bg-card rounded-sm p-3 mb-3 print:hidden">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">From Date</Label>
          <Input data-testid={`${prefix}-date-from`} type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9 w-40"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">To Date</Label>
          <Input data-testid={`${prefix}-date-to`} type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9 w-40"/>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4 print:hidden">{cards}</div>
          <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${prefix}-ledger-table`}>
              <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Reference No</th>
                  <th className="text-left p-3">Transaction Type</th>
                  <th className="text-left p-3">Voucher No</th>
                  <th className="text-right p-3">Debit</th>
                  <th className="text-right p-3">Credit</th>
                  <th className="text-right p-3">Running Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No entries in the selected range</td></tr>}
                {pg2.pageItems.map((r, i) => {
                  const isVoucher = r.voucher_no && r.voucher_no !== "-";
                  return (
                    <tr key={i} data-testid={`${prefix}-row-${i}`} className="border-t border-border hover:bg-secondary/40">
                      <td className="p-3 font-mono text-xs">{r.date}</td>
                      <td className="p-3 font-mono text-xs text-primary uppercase">{isVoucher ? "-" : r.ref}</td>
                      <td className="p-3 text-xs">{r.particulars}</td>
                      <td className="p-3 font-mono text-xs">{r.voucher_no || "-"}</td>
                      <td className="p-3 text-right font-mono text-xs">{r.dr ? inr(r.dr) : "-"}</td>
                      <td className="p-3 text-right font-mono text-xs">{r.cr ? inr(r.cr) : "-"}</td>
                      <td className="p-3 text-right font-mono">{inr(r.balance)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <PaginationBar pg={pg2} testid="pagination-2"/>
          </div>
        </>
      )}
    </div>
  );
}

function SupplierTab() {
  const [vendors, setVendors] = useState([]);
  const [outstanding, setOutstanding] = useState({});
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/vendors").then(r => setVendors(r.data)).catch(e => toast.error(formatErr(e)));
    api.get("/reports/vendor-outstanding").then(r => setOutstanding(Object.fromEntries(r.data.map(x => [x.id, x.outstanding])))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sel) { setData(null); return; }
    const p = new URLSearchParams();
    if (from) p.set("from_date", from);
    if (to) p.set("to_date", to);
    const t = setTimeout(() => {
      api.get(`/reports/vendor-ledger/${sel}?${p}`).then(r => setData(r.data)).catch(e => toast.error(formatErr(e)));
    }, 200);
    return () => clearTimeout(t);
  }, [sel, from, to]);

  const rows = useMemo(() => vendors.map(v => ({ ...v, gstin: v.gstin, outstanding: outstanding[v.id] ?? (v.opening_balance || 0) })), [vendors, outstanding]);
  const party = vendors.find(v => v.id === sel);

  if (!sel) return <PartyList rows={rows} search={search} setSearch={setSearch} onPick={setSel} nameLabel="Supplier Name" prefix="sup"/>;

  return (
    <LedgerDetail prefix="sup" title={party?.name || "Supplier"} subtitle={[party?.phone, party?.gstin].filter(Boolean).join(" · ")}
      data={data} from={from} to={to} setFrom={setFrom} setTo={setTo} onBack={() => { setSel(null); setData(null); }}
      cards={data && (
        <>
          <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total Payments (Dr)</div><div className="font-display font-black text-xl mt-1 text-green-500">{inr(data.total_dr)}</div></div>
          <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total Purchases (Cr)</div><div className="font-display font-black text-xl mt-1">{inr(data.total_cr)}</div></div>
          <div className="border border-primary/40 bg-primary/5 rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-primary">Payable Due</div><div className={`font-display font-black text-xl mt-1 ${data.closing_balance < 0 ? "text-destructive" : "text-primary"}`} data-testid="sup-outstanding">{inr(-data.closing_balance)}</div></div>
        </>
      )}/>
  );
}

function CustomerTab() {
  const [customers, setCustomers] = useState([]);
  const [outstanding, setOutstanding] = useState({});
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/customers").then(r => setCustomers(r.data)).catch(e => toast.error(formatErr(e)));
    api.get("/reports/customer-outstanding").then(r => setOutstanding(Object.fromEntries(r.data.map(x => [x.id, x.outstanding])))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sel) { setData(null); return; }
    const p = new URLSearchParams();
    if (from) p.set("from_date", from);
    if (to) p.set("to_date", to);
    const t = setTimeout(() => {
      api.get(`/reports/customer-ledger/${sel}?${p}`).then(r => setData(r.data)).catch(e => toast.error(formatErr(e)));
    }, 200);
    return () => clearTimeout(t);
  }, [sel, from, to]);

  const rows = useMemo(() => customers.map(c => ({ ...c, gstin: c.gst_number, outstanding: outstanding[c.id] ?? (c.opening_balance || 0) })), [customers, outstanding]);
  const party = customers.find(c => c.id === sel);

  if (!sel) return <PartyList rows={rows} search={search} setSearch={setSearch} onPick={setSel} nameLabel="Customer Name" prefix="cust"/>;

  return (
    <LedgerDetail prefix="cust" title={party?.name || "Customer"} subtitle={[party?.phone, party?.gst_number].filter(Boolean).join(" · ")}
      data={data} from={from} to={to} setFrom={setFrom} setTo={setTo} onBack={() => { setSel(null); setData(null); }}
      cards={data && (
        <>
          <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total Sales (Dr)</div><div className="font-display font-black text-xl mt-1">{inr(data.total_dr)}</div></div>
          <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total Receipts (Cr)</div><div className="font-display font-black text-xl mt-1 text-green-500">{inr(data.total_cr)}</div></div>
          <div className="border border-primary/40 bg-primary/5 rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-primary">Outstanding Due</div><div className={`font-display font-black text-xl mt-1 ${data.closing_balance > 0 ? "text-destructive" : "text-primary"}`} data-testid="cust-outstanding">{inr(data.closing_balance)}</div></div>
        </>
      )}/>
  );
}

export default function Ledger() {
  const [tab, setTab] = useState("supplier");
  return (
    <div className="p-8 print:p-4">
      <div className="mb-6 print:mb-3">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2 print:hidden"><BookText className="w-3 h-3"/> Accounts</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Ledger</h1>
        <div className="text-xs text-muted-foreground mt-1">Supplier and customer account ledgers</div>
      </div>

      <div className="flex gap-2 mb-4 border-b border-border print:hidden">
        <button data-testid="tab-supplier" onClick={() => setTab("supplier")}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${tab === "supplier" ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <Truck className="w-3 h-3"/>Supplier
        </button>
        <button data-testid="tab-customer" onClick={() => setTab("customer")}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${tab === "customer" ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <Users className="w-3 h-3"/>Customer
        </button>
      </div>

      {tab === "supplier" ? <SupplierTab/> : <CustomerTab/>}
    </div>
  );
}
